import React from "react";
import {TaggedLogger} from "@nextlot/core/utilities";
import NetworkDisconnectedAlertPanelOverlayCmp from "../NetworkDisconnectedAlertPanelOverlayCmp";
import WebcastPageFooterLineCmp from "../WebcastPageFooterLineCmp";
import useWebcastConnect from "@nextlot/core/webcast/hooks/useWebcastConnect";
import {RoundStopTypeEnum, RoundTypeEnum, WebcastStateEnum} from "@nextlot/core/webcast/data/WebcastDataDomain";
import {ReduxWebcastCurrentRoundType} from "@nextlot/core/webcast/redux/webcast/reducerWebcastCurrentRound";
import {ReduxWebcastCurrentRoundBiddingType} from "@nextlot/core/webcast/redux/webcast/reducerWebcastCurrentRoundBidding";
import OnsiteInfoHeaderCmp from "./OnsiteInfoHeaderCmp";
import {Lot} from "@nextlot/core/webcast/data/data_types_definitions";
import {CurrencyAmountDisplay} from "../CurrencyAmountDisplay";
import {useAppSelector} from "@nextlot/core/webcast/hooks";


const _logger = TaggedLogger.get('OnsiteRootCmp');

export default function OnsiteRootCmp() {
    _logger.debug('.render()');
    const webcastStatusState = useWebcastConnect();
    return (
        <>
            <NetworkDisconnectedAlertPanelOverlayCmp />

            {
                (webcastStatusState === WebcastStateEnum.WEBCAST_STATE__CLOSED)
                    ? <FinishedAppStateViewCmp />
                    : (webcastStatusState > WebcastStateEnum.WEBCAST_STATE__LOBBY)
                        ? <LoadedAppStateViewCmp />
                        : <LoadingAppStateViewCmp />
            }

            <WebcastPageFooterLineCmp screenName={'Onsite'}/>
        </>
    )
}


function FinishedAppStateViewCmp() {
    return (
        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
            <h3 className="display-2">The webcast event has closed. Thank you.</h3>
        </div>
    )
}

function LoadedAppStateViewCmp() {

    const webcastAuctionData = useAppSelector((state) => state.activeWebcast.webcastAuctionData);
    const currentRound = useAppSelector((state) => state.activeWebcast.webcastCurrentRound);
    const currentRoundBidding = useAppSelector((state) => state.activeWebcast.webcastCurrentRoundBidding);

    const [currentIndex, setCurrentIndex] = React.useState<number>(0);

    React.useEffect(() => {
        if (! currentRound?.lotsIds?.length) {
            // currentRound is not yet defined — this is only possible for a re-render right after LOBBY state
            setCurrentIndex(0);
            return
        }

        const maxIndex = currentRound.lotsIds.length;
        const carouselInterval = setInterval(() => {
            setCurrentIndex(prevIndex => {
                const newIndex = prevIndex + 1;
                return (newIndex >= maxIndex) ? 0 : newIndex;
            })
        }, 6000);


        return () => {
            clearInterval(carouselInterval);
        }
    }, [currentRound]);

    React.useEffect(() => {
      // Resets currentIndex to 0 for every new round
      setCurrentIndex(0);
    }, [currentRound.roundId]);


    const lotsMapById = webcastAuctionData.lotsMapById;
    const lots: Array<Lot> = currentRound.lotsIds.map(lotId => lotsMapById.get(lotId));

    return (
        <div className="d-flex flex-column flex-grow-1">
            <OnsiteInfoHeaderCmp currentRound={currentRound} lotsMapById={lotsMapById}/>
            <div className="onsite-content-wrapper row mb-3 mx-1 py-2 flex-grow-1">
                <div className="col-7 d-flex flex-column flex-grow-1">
                    <div className="d-flex flex-column flex-grow-1">
                      {
                        (lots.length >= currentIndex + 1) // Guard for out of bounds index
                        &&
                        <>
                          <CurrentLotItem lots={lots} currentIndex={currentIndex}/>

                          <div className="mt-3 mb-auto onsite-lot-details-wrapper">
                              <h1>Lot #{lots[currentIndex].number}:</h1>
                              <h1>{lots[currentIndex].name}</h1>
                          </div>
                        </>
                      }

                    </div>
                </div>

                <div className="col-5 d-flex flex-column flex-grow-1">
                    <BidDetails currentRound={currentRound} currentRoundBidding={currentRoundBidding} currentLot={lots[currentIndex]} />
                </div>
            </div>
        </div>
    )
}

function LoadingAppStateViewCmp() {
    return (
        <div className="w-100 h-100 d-flex justify-content-center align-items-center">
            <h3 className="display-2">Loading, please wait ...</h3>
        </div>
    )
}

function CurrentLotItem(props: { lots: Array<Lot>, currentIndex: number }) {
    const {lots, currentIndex} = props;
    //TODO: fix this problem in componentDidUpdate
    if (currentIndex > lots.length - 1) {
        return (
            <div className="mt-auto onsite-img-wrapper">
                {lots[currentIndex].images && lots[currentIndex].images.length > 0 &&
                    <img src={lots[currentIndex].images[0].mdUrl}/>
                }
            </div>
        )
    }

    return (
        <div className="mt-auto onsite-img-wrapper">
            {lots[currentIndex].images && lots[currentIndex].images.length > 0 &&
                <img src={lots[currentIndex].images[0].mdUrl}/>
            }
        </div>
    )
}

function BidDetails(props: { currentRound: ReduxWebcastCurrentRoundType, currentRoundBidding: ReduxWebcastCurrentRoundBiddingType, currentLot: Lot }) {
    const {currentRound, currentRoundBidding, currentLot} = props;
    const {askingBidValue, highestBid} = props.currentRoundBidding;

    if (currentRound.stopType === RoundStopTypeEnum.CANCEL) {
        return <h3 className="display-2">Cancel</h3>
    } else if (currentRound.stopType === RoundStopTypeEnum.NO_SALE) {
        return <h3 className="display-2">No Sale</h3>
    } else if (highestBid) {
        return (
            <div className="onsite-bid-details d-flex flex-column flex-grow-1 justify-content-center">
                <div className="mb-5 mb-3">
                    <h1 className={`display-2 mb-3 ${highestBid.bidderDisplay === "FLOOR" ? 'floor-bid' : 'online-bid'}`}>
                        {highestBid.bidderDisplay}
                    </h1>
                    <h1 className="display-1 fw-bold">
                        <CurrencyAmountDisplay className={currentRound.stopType === RoundStopTypeEnum.SOLD ? 'flash':''} valueCents={highestBid.value}/>
                    </h1>
                    {
                        currentRound.roundType === RoundTypeEnum.SINGLE_BOXLOT &&
                        <h1 className="fw-bold">
                            <span> x {currentLot.itemsQty} = <CurrencyAmountDisplay valueCents={highestBid.value * currentLot.itemsQty}/></span>
                        </h1>
                    }
                </div>
                <div className="mt-5">

                    {askingBidValue > 0 ? <h1 className="display-2">Asking: <CurrencyAmountDisplay valueCents={askingBidValue}/></h1>:''}
                    {currentRound.stopType === RoundStopTypeEnum.PENDING
                        ? (currentRound.roundType === RoundTypeEnum.GROUP_CHOICE || currentRound.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN)
                            ? ((currentRoundBidding.choiceLotsSelection && currentRoundBidding.choiceLotsSelection.length)
                                    ? <h3 className="display-2">Pending</h3>
                                    : ''
                            )
                            : <h3 className="display-2">Pending</h3>
                        : ''
                    }
                    {currentRound.stopType === RoundStopTypeEnum.SOLD
                        ? (currentRound.roundType === RoundTypeEnum.GROUP_CHOICE || currentRound.roundType === RoundTypeEnum.GROUP_CHOICE_REOPEN)
                            ? ((currentRoundBidding.choiceLotsSelection && currentRoundBidding.choiceLotsSelection.length)
                                    ? <h3 className="display-2">Sold</h3>
                                    : ''
                            )

                            : <h3 className="display-2">Sold</h3>
                        : ''
                    }
                </div>
            </div>
        )
    }


    return <h1 className="display-1">Waiting for bidding to start</h1>
}
